import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import {
  giveawayService,
  Giveaway,
  CreateGiveawayRequest,
  UpdateGiveawayRequest,
} from '../../api/services/giveawayService';

export const useGiveaways = () => {
  const [giveaways, setGiveaways] = useState<Giveaway[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<Giveaway | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Id currently being shown/hidden, so only that row's switch spins. */
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchGiveaways = useCallback(async () => {
    setLoading(true);
    try {
      const result = await giveawayService.getGiveaways();
      setGiveaways(Array.isArray(result) ? result : []);
    } catch {
      message.error('Failed to load giveaways');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGiveaways();
  }, [fetchGiveaways]);

  const handleCreate = async (values: CreateGiveawayRequest) => {
    setSubmitting(true);
    try {
      await giveawayService.createGiveaway(values);
      message.success('Giveaway created');
      setModalVisible(false);
      fetchGiveaways();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to create giveaway',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, values: UpdateGiveawayRequest) => {
    setSubmitting(true);
    try {
      await giveawayService.updateGiveaway(id, values);
      message.success('Giveaway updated');
      setModalVisible(false);
      fetchGiveaways();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to update giveaway',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * The show/hide switch. ACTIVE is the only status the app renders, so
   * hiding parks it back in DRAFT — the giveaway and all of its entries are
   * kept, it simply stops appearing on the profile screen.
   */
  const handleToggleVisible = async (id: string, visible: boolean) => {
    setTogglingId(id);
    try {
      await giveawayService.updateGiveaway(id, {
        status: visible ? 'ACTIVE' : 'DRAFT',
      });
      message.success(visible ? 'Giveaway is now live' : 'Giveaway hidden');
      fetchGiveaways();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to change visibility',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await giveawayService.deleteGiveaway(id);
      message.success('Giveaway deleted');
      fetchGiveaways();
    } catch (e) {
      message.error(
        (e as { message?: string })?.message || 'Failed to delete giveaway',
      );
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    setModalVisible(true);
  };

  const openEditModal = (giveaway: Giveaway) => {
    setEditing(giveaway);
    setModalVisible(true);
  };

  return {
    giveaways,
    loading,
    modalVisible,
    editing,
    submitting,
    togglingId,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleToggleVisible,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch: fetchGiveaways,
  };
};
